import { z } from 'zod';

import {
  DAY_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  EXPERIENCE_TYPE_OPTIONS,
  EXPERIENCE_YEARS_OPTIONS,
} from './careers';
import { calendarDateSchema } from './employee';
import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';
import { emailSchema, usernameSchema } from './user';

/*
 * The applicant pipeline — ROADMAP.md §9.10, data-model.md §7.
 *
 * **The office's half of the careers form.** `./careers.ts` is the applicant's
 * half and owns the option lists; this file does not restate them, because a
 * second copy of "Days Available" is a second thing to get out of step with the
 * form that collected it.
 *
 * Two rules shape everything here:
 *
 *   - **An application is five tables, and a view that returns one of them is a
 *     silent discard.** Employment history, references, days available and types
 *     of cleaning experience are child rows, and they are the parts a summary
 *     drops without anybody noticing. `ApplicantDetail` carries all four.
 *   - **`job_application` is PII.** Admin-only, never logged, kept out of
 *     general exports. That is why there is no public or employee-facing shape
 *     in this file at all — there is nowhere else for it to go.
 */

// ---------------------------------------------------------------------------
// Status — the prototype's five states
// ---------------------------------------------------------------------------

export const APPLICATION_STATUS_VALUES = [
  'new',
  'reviewing',
  'interviewing',
  'hired',
  'not_hired',
] as const;
export type ApplicationStatusValue = (typeof APPLICATION_STATUS_VALUES)[number];

/**
 * What a person reads. Never the stored key — CLAUDE.md §4, defect P5.
 *
 * **"Not hired" is the state; "Not hiring" is the button.** They are different
 * words on purpose and that is the one place in this file worth pausing on: the
 * badge describes what happened to the application, the action describes what
 * the office is doing. Everywhere the *state* is named it comes from this map,
 * so the P11/P18 defect — three words for one state, on three screens — cannot
 * repeat here.
 */
export const APPLICATION_STATUS_LABELS: Record<ApplicationStatusValue, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  interviewing: 'Interviewing',
  hired: 'Hired',
  not_hired: 'Not hired',
};

/**
 * Which statuses can follow which — the prototype's contextual actions.
 *
 * `new` → Review · `reviewing` → Interview / Hire / Not hiring · `hired` → View
 * Employee. Two of them are worth stating out loud because they are decisions
 * rather than transcription:
 *
 *   - **`hired` is not in any list here.** It is reachable only through
 *     `POST /:id/hire`, which creates the employee. A status field that could be
 *     set to `hired` on its own would produce an application saying somebody was
 *     hired with no employee record behind it, and the office would find out
 *     when they went looking for the person.
 *   - **`not_hired` can go back to `reviewing`.** The prototype offers no way
 *     back, which makes a mis-clicked "Not hiring" unrecoverable without SQL.
 *     There is a real person at the other end of the row; one wrong click should
 *     not be the end of it.
 */
export const APPLICATION_STATUS_TRANSITIONS: Record<
  ApplicationStatusValue,
  readonly ApplicationStatusValue[]
> = {
  new: ['reviewing', 'not_hired'],
  reviewing: ['interviewing', 'not_hired'],
  interviewing: ['reviewing', 'not_hired'],
  // Terminal. An employee exists; undoing that is a decision about a person's
  // job, not a status change.
  hired: [],
  not_hired: ['reviewing'],
};

export const APPLICATION_STATUS_FILTERS = [
  { value: 'new', label: APPLICATION_STATUS_LABELS.new },
  { value: 'reviewing', label: APPLICATION_STATUS_LABELS.reviewing },
  { value: 'interviewing', label: APPLICATION_STATUS_LABELS.interviewing },
  { value: 'hired', label: APPLICATION_STATUS_LABELS.hired },
  { value: 'not_hired', label: APPLICATION_STATUS_LABELS.not_hired },
  { value: 'all', label: 'All' },
] as const;

// ---------------------------------------------------------------------------
// Ordering the two multi-selects
// ---------------------------------------------------------------------------

/*
 * **Child rows come back in whatever order the database feels like**, and for
 * these two that is a bug rather than an inconvenience: days sorted by their
 * stored key read "friday, monday, saturday, sunday, thursday" — which is not a
 * week, and an office scanning for weekend cover has to read all seven to find
 * out. Both are sorted into the order the form offered them.
 */
const DAY_ORDER = DAY_OPTIONS.map((option) => option.value);
const EXPERIENCE_TYPE_ORDER = EXPERIENCE_TYPE_OPTIONS.map((option) => option.value);

function byOrder(order: readonly string[]) {
  return (a: string, b: string) => order.indexOf(a) - order.indexOf(b);
}

export const byWeekOrder = byOrder(DAY_ORDER);
export const byExperienceTypeOrder = byOrder(EXPERIENCE_TYPE_ORDER);

/** A stored key with its display label, so a screen never has to look one up. */
export interface LabelledValue {
  value: string;
  label: string;
}

// ---------------------------------------------------------------------------
// What the office reads
// ---------------------------------------------------------------------------

/**
 * One row in the pipeline list.
 *
 * Deliberately not the whole application: this is the screen somebody scans, and
 * the fields here are the ones they scan *for* — who, for what job, when they
 * applied, and the three facts that decide whether to ring them today.
 * Everything else is one click away in the detail, which is a different
 * endpoint rather than a truncation of this one.
 */
export interface ApplicantListItem {
  id: string;
  fullName: string;
  email: string;
  phone: string;

  positionId: string;
  /** `position.name` — the display value, never the id (P5). */
  positionName: string | null;

  status: ApplicationStatusValue;
  statusLabel: string;

  employmentTypeLabel: string;
  /** Null when they said they have none — which is not the same as unknown. */
  experienceYearsLabel: string | null;
  hasReliableTransportation: boolean;

  submittedAt: string;
  reviewedAt: string | null;
  convertedEmployeeId: string | null;
}

export interface ApplicantEmployer {
  id: string;
  sortOrder: number;
  employerName: string;
  jobTitle: string;
  datesWorked: string | null;
  responsibilities: string | null;
  reasonForLeaving: string | null;
}

export interface ApplicantReference {
  id: string;
  sortOrder: number;
  name: string;
  phone: string;
  relationship: string | null;
}

/**
 * The whole application, across all five tables.
 *
 * **The four fields below the contact block are the reason this interface is
 * written out rather than derived from the row type.** `employers`,
 * `references`, `daysAvailable` and `experienceTypes` live in child tables, and
 * a detail view that returns the parent row and stops is exactly the discard
 * 8.7 was written to prevent — one size smaller and just as invisible.
 */
export interface ApplicantDetail extends ApplicantListItem {
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;

  desiredPay: string | null;
  availableStartDate: string | null;
  employmentType: string;

  hasCleaningExperience: boolean;
  experienceYears: string | null;
  hasOwnSupplies: boolean;
  hasWorkExperience: boolean;
  hasReferences: boolean;

  additionalInfo: string | null;
  internalNotes: string | null;

  reviewedByUserId: string | null;
  reviewedByName: string | null;

  /** Set once hired. The link to the person's employee record. */
  convertedEmployeeName: string | null;

  employers: ApplicantEmployer[];
  references: ApplicantReference[];
  /** In week order, not key order. See `byWeekOrder`. */
  daysAvailable: LabelledValue[];
  /** In the form's own order. */
  experienceTypes: LabelledValue[];
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export const applicantListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...APPLICATION_STATUS_VALUES, 'all']).default('new'),
  positionId: z.uuid().optional(),
});
export type ApplicantListQuery = z.infer<typeof applicantListQuerySchema>;

/**
 * A status move.
 *
 * `hired` is absent from the enum, not merely refused by the service — a shape
 * the API cannot express is one no future caller can talk itself into.
 */
export const applicantStatusSchema = z.object({
  status: z.enum(['reviewing', 'interviewing', 'not_hired']),
});
export type ApplicantStatusInput = z.infer<typeof applicantStatusSchema>;

/** The office's own remarks. Never sent anywhere, never shown to the applicant. */
export const applicantNotesSchema = z.object({
  internalNotes: z
    .string()
    .trim()
    .max(5000, 'That note is longer than our system can take — please shorten it.')
    .transform((value) => (value.length === 0 ? null : value))
    .nullable(),
});
export type ApplicantNotesInput = z.infer<typeof applicantNotesSchema>;

/**
 * Hiring someone: the application becomes an employee.
 *
 * **The name is split by a human, here, and that is the whole point of this
 * shape.** The careers form has one Name field, so `job_application.full_name`
 * holds "Maria de la Cruz" as one string — and `employee` needs a first and a
 * last name. Splitting that automatically is guesswork on any name that is not
 * exactly two words, and it is not undoable once stored. So the dialog suggests
 * a split, a person confirms it, and what arrives here is a decision rather than
 * a guess. data-model.md §7 says the same thing about `lead`.
 *
 * Everything else defaults from the application and stays editable: the login
 * email, the position applied for, the address they gave.
 */
export const hireApplicantSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Give a first name').max(128),
    lastName: z.string().trim().min(1, 'Give a last name').max(128),

    /*
     * The same two identity rules every account obeys — ./user.ts, defect P9.
     * An applicant always gave an email, so in practice this is prefilled and
     * left alone; a username-only cleaner is still possible and still needs a
     * recovery address.
     */
    email: emailSchema.optional(),
    username: usernameSchema.optional(),
    recoveryEmail: emailSchema.optional(),

    maxJobsPerDay: z
      .number()
      .int('Give a whole number of jobs')
      .min(1, 'At least one job a day')
      .max(20, 'More than twenty jobs a day is not a real number')
      .default(3),

    hourlyRateCents: z
      .number()
      .int('Money is whole cents')
      .min(0, 'A rate cannot be negative')
      .max(100_000_000)
      .nullable()
      .optional(),

    /** Defaults to the position they applied for. */
    positionId: z.uuid().nullable().optional(),
    hireDate: calendarDateSchema.nullable().optional(),
    skillIds: z.array(z.uuid()).max(100).optional(),
  })
  .refine((value) => Boolean(value.email ?? value.username), {
    message: 'Give them an email address or a username to sign in with',
    path: ['email'],
  })
  .refine((value) => Boolean(value.email ?? value.recoveryEmail), {
    message: 'Add a recovery email — without one this account can never be reset or contacted',
    path: ['recoveryEmail'],
  });
export type HireApplicantInput = z.infer<typeof hireApplicantSchema>;

/**
 * What hiring returns.
 *
 * `temporaryPassword` is **null on the second call** and that is not an
 * oversight. Hiring is idempotent: calling it again returns the employee the
 * first call made, and there is no password to return because none was
 * generated — the one that was is not stored in readable form anywhere. The
 * screen says so rather than showing an empty box.
 */
export interface HireApplicantResult {
  applicationId: string;
  employeeId: string;
  employeeName: string;
  /** True only on the call that actually created the employee. */
  created: boolean;
  temporaryPassword: string | null;
}

/** The list, plus a count per pill so "New 3" is visible before a click. */
export interface ApplicantListResponse {
  data: ApplicantListItem[];
  page: number;
  pageSize: number;
  total: number;
  counts: Record<ApplicationStatusValue | 'all', number>;
}

// ---------------------------------------------------------------------------
// Splitting one name into two
// ---------------------------------------------------------------------------

/**
 * A first guess at the split: the last word is the surname, everything before it
 * is the given name.
 *
 * **A suggestion, never an answer.** Right for "Jane Smith"; right for "Maria de
 * la Cruz" only by luck; wrong for anyone whose family name comes first. The
 * hire dialog prefills two editable fields with it and shows the original
 * beside them, so what reaches the database is a person's decision rather than
 * this function's guess — which is what data-model.md §7 means by "a human
 * separates them if it ever matters".
 *
 * Here rather than in the dialog so it can be tested without rendering
 * anything, and because it is a fact about `full_name` rather than about a
 * form.
 */
export function suggestNameSplit(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0]!, lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1]!,
  };
}

// ---------------------------------------------------------------------------
// Labels — re-exported so a screen has one import, not five
// ---------------------------------------------------------------------------

export { DAY_OPTIONS, EMPLOYMENT_TYPE_OPTIONS, EXPERIENCE_TYPE_OPTIONS, EXPERIENCE_YEARS_OPTIONS };
